import { useMemo, useState, type FormEvent } from "react";
import type { LocationRow, PlantSymbol } from "@garden/types/db";
import type { PlantCreateInput } from "../db/schemas";
import { LocationCreateModal } from "./LocationCreateModal";
import { SymbolPicker } from "./SymbolPicker";
import { BedPositionPicker } from "./BedPositionPicker";
import { parsePolygon } from "../lib/mapData";
import "./PlantForm.css";

export interface PlantFormValues {
  common_name: string;
  scientific_name: string;
  cultivar: string;
  location_id: string; // "" means none
  planted_date: string; // "" or YYYY-MM-DD
  source_acquisition: string;
  status: "alive" | "dead" | "removed" | "needs_identification";
  notes: string;
  symbol: PlantSymbol | null;
  /** Normalised 0..1 position within the chosen location's bbox. Null = unplaced. */
  position: { x: number; y: number } | null;
}

export const emptyForm: PlantFormValues = {
  common_name: "",
  scientific_name: "",
  cultivar: "",
  location_id: "",
  planted_date: "",
  source_acquisition: "",
  status: "alive",
  notes: "",
  symbol: null,
  position: null,
};

interface PlantFormProps {
  initial?: Partial<PlantFormValues>;
  locations: LocationRow[];
  submitLabel: string;
  onSubmit: (input: PlantCreateInput) => Promise<void>;
  onCancel?: () => void;
  onLocationCreated: (loc: LocationRow) => void;
  gardenId: string;
}

export function PlantForm({
  initial,
  locations,
  submitLabel,
  onSubmit,
  onCancel,
  onLocationCreated,
  gardenId,
}: PlantFormProps) {
  const [values, setValues] = useState<PlantFormValues>({ ...emptyForm, ...initial });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const update = <K extends keyof PlantFormValues>(key: K, val: PlantFormValues[K]) => {
    setValues((v) => ({ ...v, [key]: val }));
  };

  const onSelectLocation = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === "__create__") {
      setShowLocationModal(true);
      return;
    }
    // Switching location invalidates any previously-picked position (different bbox).
    setValues((s) => ({ ...s, location_id: v, position: null }));
  };

  const selectedLocation = useMemo(
    () => (values.location_id ? locations.find((l) => l.id === values.location_id) ?? null : null),
    [locations, values.location_id],
  );

  const selectedPolygon = useMemo(
    () => (selectedLocation ? parsePolygon(selectedLocation.polygon) : null),
    [selectedLocation],
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!values.common_name.trim()) {
      setError("Common name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: PlantCreateInput = {
        common_name: values.common_name.trim(),
        scientific_name: values.scientific_name.trim() || null,
        cultivar: values.cultivar.trim() || null,
        location_id: values.location_id || null,
        planted_date: values.planted_date || null,
        source_acquisition: values.source_acquisition.trim() || null,
        status: values.status,
        notes: values.notes || null,
        symbol: values.symbol,
        position: values.position,
      };
      await onSubmit(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save plant.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <form className="PlantForm" onSubmit={handleSubmit} noValidate>
        <div className="PlantForm__row">
          <label className="PlantForm__field">
            <span>Common name</span>
            <input
              type="text"
              value={values.common_name}
              onChange={(e) => update("common_name", e.target.value)}
              required
              autoFocus
              placeholder="Climbing rose"
            />
          </label>
          <label className="PlantForm__field">
            <span>Scientific name <em>optional</em></span>
            <input
              type="text"
              value={values.scientific_name}
              onChange={(e) => update("scientific_name", e.target.value)}
              placeholder="Rosa 'Pierre Ronsard'"
            />
          </label>
        </div>

        <div className="PlantForm__row">
          <label className="PlantForm__field">
            <span>Cultivar <em>optional</em></span>
            <input
              type="text"
              value={values.cultivar}
              onChange={(e) => update("cultivar", e.target.value)}
              placeholder="Pierre Ronsard"
            />
          </label>
          <label className="PlantForm__field">
            <span>Location</span>
            <select value={values.location_id} onChange={onSelectLocation}>
              <option value="">— none —</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.kind})
                </option>
              ))}
              <option value="__create__">+ Create new location…</option>
            </select>
          </label>
        </div>

        <div className="PlantForm__row">
          <label className="PlantForm__field">
            <span>Planted date <em>optional</em></span>
            <input
              type="date"
              value={values.planted_date}
              onChange={(e) => update("planted_date", e.target.value)}
            />
          </label>
          <label className="PlantForm__field">
            <span>Status</span>
            <select
              value={values.status}
              onChange={(e) => update("status", e.target.value as PlantFormValues["status"])}
            >
              <option value="alive">Alive</option>
              <option value="needs_identification">Needs identification</option>
              <option value="dead">Dead</option>
              <option value="removed">Removed</option>
            </select>
          </label>
        </div>

        <div className="PlantForm__field">
          <span>
            Map symbol <em>{values.symbol ? "" : "optional — required to show on the map"}</em>
          </span>
          <SymbolPicker value={values.symbol} onChange={(s) => update("symbol", s)} />
        </div>

        {selectedPolygon && (
          <div className="PlantForm__field">
            <span>
              Position on {selectedLocation?.name ?? "bed"}{" "}
              <em>{values.position ? "" : "click on the bed to place this plant"}</em>
            </span>
            <BedPositionPicker
              polygon={selectedPolygon}
              kind={selectedLocation?.kind ?? "bed"}
              position={values.position}
              symbol={values.symbol}
              onChange={(p) => update("position", p)}
            />
          </div>
        )}
        {values.location_id && !selectedPolygon && (
          <p className="PlantForm__note">
            This location has no polygon yet — the plant will be saved without a map
            position. You can place it later once the location has a shape.
          </p>
        )}

        <label className="PlantForm__field">
          <span>Source / acquisition <em>optional</em></span>
          <input
            type="text"
            value={values.source_acquisition}
            onChange={(e) => update("source_acquisition", e.target.value)}
            placeholder="Crocus.co.uk · April 2026"
          />
        </label>

        <label className="PlantForm__field">
          <span>Notes <em>plain text — markdown comes in stage 8</em></span>
          <textarea
            value={values.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={6}
            placeholder="Anything you want to remember about this plant…"
          />
        </label>

        {error && <p className="PlantForm__error">{error}</p>}

        <div className="PlantForm__actions">
          {onCancel && (
            <button
              type="button"
              className="PlantForm__btn PlantForm__btn--ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="PlantForm__btn PlantForm__btn--primary"
            disabled={submitting}
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </div>
      </form>

      {showLocationModal && (
        <LocationCreateModal
          gardenId={gardenId}
          onClose={() => setShowLocationModal(false)}
          onCreated={(loc) => {
            setShowLocationModal(false);
            onLocationCreated(loc);
            setValues((s) => ({ ...s, location_id: loc.id, position: null }));
          }}
        />
      )}
    </>
  );
}
